import { useAnimate, type AnimationPlaybackControls } from 'motion/react';
import { useEffect, useSyncExternalStore } from 'react';

/**
 * THE AMBIENT BACKDROP — atmosphere behind every screen, never a feature.
 *
 * Mounted ONCE, in `main.tsx`, as a fixed layer under the router, so the
 * sign-in screen and the shell share the same canvas. Everything a screen
 * paints keeps its own `bg-card` / `bg-background`, so nothing here changes
 * the contrast of a single piece of text — the census in
 * `src/test/colour-contrast-census.test.ts` measures ink against those
 * surfaces and this layer carries no ink at all.
 *
 * Three layers, bottom to top:
 *
 *   1. ORBS — three large radial-gradient glows tinted from the person's
 *      accent (`--backdrop-orb-*` in index.css derive from `--primary`, which
 *      is the accent once `data-accent` is set, so a new accent re-tints the
 *      room with no re-render). They drift on slow, independent loops.
 *   2. GRID — a 1px dot every 24px, drawn as an SVG mask over a foreground
 *      tint, faded radially from the top-start corner (RTL flips it through
 *      `--backdrop-grid-origin`) so it has gone by the lower third.
 *   3. GRAIN — a static feTurbulence tile at a few percent, soft-light, so
 *      the flat surface reads as paper rather than as a screen.
 *
 * MOTION, AND WHY IT IS NOT CSS. `flash-rate.test.ts` forbids hand-written
 * CSS keyframe and animation declarations in this tree so every period stays
 * readable. The drift therefore runs through `motion`'s `animate()`, with loops of
 * 38–59 seconds — three orders of magnitude under the three-a-second bar —
 * and transform/opacity only, so it composites on the GPU. The playback
 * controls are PAUSED while the tab is hidden and resumed on return, through
 * `visibilitychange` rather than any timer. Under `prefers-reduced-motion`
 * the orbs are drawn at their rest positions and nothing moves.
 *
 * `data-backdrop` on the root is the hook a future "disable backdrop" toggle
 * can select on; nothing reads it today.
 */

const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';

function subscribeReducedMotion(onChange: () => void): () => void {
  const query = window.matchMedia?.(REDUCED_MOTION);
  if (!query?.addEventListener) return () => {};
  query.addEventListener('change', onChange);
  return () => query.removeEventListener('change', onChange);
}

function readReducedMotion(): boolean {
  return window.matchMedia?.(REDUCED_MOTION)?.matches ?? false;
}

/**
 * Read live rather than cached at module load, so a test (or a person
 * flipping the OS switch) sees the change on the next render.
 */
function usePrefersReducedMotion(): boolean {
  return useSyncExternalStore(subscribeReducedMotion, readReducedMotion, () => false);
}

interface Orb {
  /** Where it rests. Logical utilities, so Arabic mirrors the composition. */
  className: string;
  tint: string;
  /** translate in % of the orb's own size, scale about its centre. */
  x: string[];
  y: string[];
  scale: number[];
  /** Seconds per loop. Co-prime-ish so the three never fall into step. */
  duration: number;
}

const ORBS: readonly Orb[] = [
  {
    className: '-start-[14%] -top-[22%] size-[62vmax]',
    tint: 'var(--backdrop-orb-1)',
    x: ['0%', '5%', '-3%', '0%'],
    y: ['0%', '-4%', '4%', '0%'],
    scale: [1, 1.06, 1.02, 1],
    duration: 41,
  },
  {
    className: '-bottom-[28%] -end-[12%] size-[56vmax]',
    tint: 'var(--backdrop-orb-2)',
    x: ['0%', '-5%', '4%', '0%'],
    y: ['0%', '3%', '-5%', '0%'],
    scale: [1, 1.04, 1.08, 1],
    duration: 53,
  },
  {
    className: 'end-[8%] top-[30%] size-[38vmax]',
    tint: 'var(--backdrop-orb-3)',
    x: ['0%', '4%', '-4%', '0%'],
    y: ['0%', '5%', '-3%', '0%'],
    scale: [1, 1.03, 1.07, 1],
    duration: 59,
  },
];

/** 1px dot every 24px. Used as a MASK so the dot colour is a theme token. */
const DOT_GRID =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24'%3E%3Ccircle cx='1' cy='1' r='1' fill='%23000'/%3E%3C/svg%3E\")";

/** A 160px film-grain tile; the browser renders it once and repeats it. */
const GRAIN =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3CfeColorMatrix type='saturate' values='0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")";

export function AmbientBackdrop() {
  const reduced = usePrefersReducedMotion();
  const [scope, animate] = useAnimate<HTMLDivElement>();

  useEffect(() => {
    if (reduced) return;
    const controls: AnimationPlaybackControls[] = ORBS.map((orb, index) =>
      animate(
        `[data-orb="${index}"]`,
        { x: orb.x, y: orb.y, scale: orb.scale },
        { duration: orb.duration, ease: 'easeInOut', repeat: Infinity, repeatType: 'loop' },
      ),
    );
    const onVisibility = () => {
      const hidden = document.visibilityState === 'hidden';
      for (const c of controls) {
        if (hidden) c.pause();
        else c.play();
      }
    };
    onVisibility();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      for (const c of controls) c.stop();
    };
  }, [animate, reduced]);

  return (
    <div
      ref={scope}
      aria-hidden="true"
      data-backdrop="ambient"
      data-motion={reduced ? 'static' : 'drift'}
      className="pointer-events-none fixed inset-0 -z-10 isolate overflow-hidden bg-background"
    >
      {ORBS.map((orb, index) => (
        <div
          key={index}
          data-orb={index}
          className={`absolute rounded-full will-change-transform ${orb.className}`}
          style={{ background: `radial-gradient(circle closest-side, ${orb.tint}, transparent)` }}
        />
      ))}
      {/* The fade is the outer mask; the dots are the inner one — two masks on
          one element would need mask-composite, which is less evenly supported. */}
      <div
        className="absolute inset-0"
        style={{
          maskImage: `radial-gradient(ellipse 90% 80% at var(--backdrop-grid-origin), #000 0%, transparent 100%)`,
          WebkitMaskImage: `radial-gradient(ellipse 90% 80% at var(--backdrop-grid-origin), #000 0%, transparent 100%)`,
        }}
      >
        <div
          className="absolute inset-0"
          style={{
            backgroundColor: 'var(--backdrop-grid)',
            maskImage: DOT_GRID,
            WebkitMaskImage: DOT_GRID,
            maskSize: '24px 24px',
            WebkitMaskSize: '24px 24px',
          }}
        />
      </div>
      <div
        className="absolute inset-0 mix-blend-soft-light"
        style={{ backgroundImage: GRAIN, backgroundSize: '160px 160px', opacity: 'var(--backdrop-grain)' }}
      />
    </div>
  );
}
